import Downshift from "downshift";
import { useMemo } from "react";
import { useTranslation } from "gatsby-plugin-react-i18next";

import { useStore } from "../../utils/ctx/SellingPageContext"
import ArrowButton from "../Atoms/ArrowButton";

const TextSearchFilter = ({ paginationTopScrollRef }) => {
  const { t } = useTranslation();
  const [store, setStore] = useStore((store) => store);
  const { searchQuery, models, brands, preFilteredBrand: filteredBrand } = useMemo(() => store, [store]);

  const autocompleteData = useMemo(() => {
    const modelsWithBrands = models.map(model => ({
      name: `${model.brand ? model.brand.name : filteredBrand} ${model.name}`,
    }))

    return [...brands, ...modelsWithBrands]
  }, [brands, models])

  const handleSearchQueryChange = (value: string) => {
    setStore({ searchQuery: value, page: 1, selectedBrands: [], selectedModels: [] })
  }

  const scrollDownToPagination = () => {
    if (paginationTopScrollRef.current && window && navigator) {
      setTimeout(() => {
        if (navigator.userAgent.indexOf("Safari") !== -1) {
          window.scrollTo({ left: 0, top: paginationTopScrollRef.current.offsetTop || 0, behavior: 'smooth' })
        } else {
          window.scrollTo(0, paginationTopScrollRef.current.offsetTop || 0);
        }
      }, 250)
    }
  }

  return (
    <Downshift
      itemToString={item => (item ? item.name : "")}
      itemCount={5}
      inputValue={searchQuery}
      stateReducer={(state, changes) => {
        switch (changes.type) {
          case Downshift.stateChangeTypes.blurInput:
          case Downshift.stateChangeTypes.mouseUp:
          case Downshift.stateChangeTypes.keyDownEnter:
            scrollDownToPagination()
            return {
              ...changes,
              inputValue: state.inputValue,
            }
          default:
            return changes;
        }
      }}
      onInputValueChange={handleSearchQueryChange}
    >
      {({
        getInputProps,
        getItemProps,
        getMenuProps,
        isOpen,
        inputValue,
        highlightedIndex,
        getRootProps,
      }) => (
        <div className="relative" {...getRootProps({}, { suppressRefError: true })}>
          <div className="flex items-center">
            <input
              className="bg-transparent w-full mt-4"
              placeholder={t("sellingHero.searchPlaceholder")}
              {...getInputProps({
                onKeyDown: event => {
                  if (event.key === 'Enter') {
                    // Prevent Downshift's default 'Enter' behavior.
                    event.nativeEvent.preventDownshiftDefault = true;

                    scrollDownToPagination();
                  }
                },
              })}
            />
            <ArrowButton className="ml-2 mt-4" isGhost={false} onClick={() => searchQuery && scrollDownToPagination()} />
          </div>
          <ul
            {...getMenuProps({
              className: "absolute w-full z-20",
              style: {
                filter:
                  "drop-shadow(8px 12px 30px rgba(163, 141, 112, 0.27))",
              },
            })}
          >
            {isOpen
              ? autocompleteData
                .filter(
                  item => !inputValue || item.name.toLowerCase().includes(inputValue.toLowerCase())
                )
                .slice(0, 8)
                .map((item, index) => (
                  <li
                    {...getItemProps({
                      key: item.name,
                      index,
                      item,
                      className: `${highlightedIndex === index
                        ? "bg-gray font-semibold"
                        : "bg-white"
                        } text-primary text-lg p-2`,
                    })}
                  >
                    {item.name}
                  </li>
                ))
              : null}
          </ul>
        </div>
      )}
    </Downshift>
  )
}

export default TextSearchFilter;
